import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Upload, Star, Trash2, ArrowUp, ArrowDown, Loader2, ImageIcon } from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { photoSrc } from "@/lib/photos";

export default function PhotoManager({ propertyId, roomId = null, title = "Foto" }) {
  const isRoom = !!roomId;
  const listUrl = isRoom ? `/rooms/${roomId}/photos` : `/properties/${propertyId}/photos`;
  const uploadUrl = listUrl;
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(null);
  const fileRef = useRef();

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    apiClient.get(listUrl).then((r) => setPhotos(r.data || [])).catch(() => setError(true)).finally(() => setLoading(false));
  }, [listUrl]);

  useEffect(() => { load(); }, [load]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        await apiClient.post(uploadUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (err) {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      }
    }
    setUploading(false);
    e.target.value = "";
    if (ok) toast.success(`${ok} foto diunggah.`);
    load();
  };

  const setCover = async (pid) => {
    setBusy(pid);
    try {
      await apiClient.post(isRoom ? `/rooms/${roomId}/photos/${pid}/cover` : `/properties/${propertyId}/photos/${pid}/cover`);
      toast.success("Cover diperbarui.");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const del = async (pid) => {
    if (!window.confirm("Hapus foto ini?")) return;
    setBusy(pid);
    try {
      await apiClient.delete(isRoom ? `/rooms/${roomId}/photos/${pid}` : `/properties/${propertyId}/photos/${pid}`);
      toast.success("Foto dihapus.");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const move = async (idx, dir) => {
    if (isRoom) return;
    const j = idx + dir;
    if (j < 0 || j >= photos.length) return;
    const arr = [...photos];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setPhotos(arr);
    try {
      await apiClient.put(`/properties/${propertyId}/photos/order`, { photo_ids: arr.map((p) => p.id) });
    } catch { load(); }
  };

  return (
    <div data-testid={isRoom ? "room-photo-manager" : "property-photo-manager"}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-heading text-lg font-semibold">{title}</h3>
          <p className="text-sm text-muted-foreground">JPEG/PNG/WEBP, maks 10 MB per file.</p>
        </div>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          data-testid="photo-upload-button"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-105 transition-transform disabled:opacity-60">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Unggah Foto
        </button>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden
          onChange={onFiles} data-testid="photo-upload-input" />
      </div>

      {loading ? (
        <div className="py-8 grid place-items-center"><Loader2 className="animate-spin text-primary" size={24} /></div>
      ) : error ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive flex items-center justify-between gap-3" data-testid="photo-error">
          <span>Gagal memuat foto.</span>
          <button onClick={load} className="font-semibold text-primary hover:underline">Coba lagi</button>
        </div>
      ) : photos.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-secondary/30 p-8 text-center" data-testid="photo-empty">
          <ImageIcon size={26} className="mx-auto mb-2 text-primary" />
          <p className="text-sm text-muted-foreground">Belum ada foto. Unggah foto pertama Anda.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="photo-grid">
          {photos.map((p, i) => (
            <div key={p.id} data-testid={`photo-item-${i}`}
              className="group relative rounded-2xl overflow-hidden border border-border bg-secondary/40 aspect-[4/3]">
              <img src={photoSrc(p)} alt={p.original_name || "foto"} className="w-full h-full object-cover" />
              {p.is_cover && (
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-primary text-primary-foreground" data-testid={`photo-cover-badge-${i}`}>
                  <Star size={10} className="fill-current" /> Cover
                </span>
              )}
              <div className="absolute inset-x-0 bottom-0 p-2 flex items-center justify-between gap-1 bg-gradient-to-t from-black/70 to-transparent">
                <div className="flex gap-1">
                  {!isRoom && (
                    <>
                      <button type="button" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`photo-move-up-${i}`}
                        className="p-1.5 rounded-lg bg-white/90 text-foreground disabled:opacity-40"><ArrowUp size={13} /></button>
                      <button type="button" onClick={() => move(i, 1)} disabled={i === photos.length - 1} data-testid={`photo-move-down-${i}`}
                        className="p-1.5 rounded-lg bg-white/90 text-foreground disabled:opacity-40"><ArrowDown size={13} /></button>
                    </>
                  )}
                </div>
                <div className="flex gap-1">
                  {!p.is_cover && (
                    <button type="button" onClick={() => setCover(p.id)} disabled={busy === p.id} data-testid={`photo-set-cover-${i}`}
                      className="p-1.5 rounded-lg bg-white/90 text-primary" title="Jadikan cover"><Star size={13} /></button>
                  )}
                  <button type="button" onClick={() => del(p.id)} disabled={busy === p.id} data-testid={`photo-delete-${i}`}
                    className="p-1.5 rounded-lg bg-white/90 text-destructive" title="Hapus"><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
